/**
 * @file shop-nav.ts
 * @description Построение меню кабинета магазина из реестра (phase19.31,
 * SC14–SC18, SC30).
 *
 * Чистые функции без React: что видит сотрудник по правам и роли, какой
 * пункт соответствует адресу (подсветка, заголовок вкладки, переход в тот
 * же раздел другого магазина), сумма счётчиков свёрнутой группы, поиск по
 * меню с синонимами. Данные — `shop-nav-registry.ts`.
 */

import type { AccountContext } from "../auth/types";
import {
  SHOP_NAV_GROUPS,
  SHOP_NAV_HOME,
  SHOP_NAV_SETTINGS,
  SHOP_SETTINGS_HUB,
  type NavCounterKey,
  type SettingsHubGroup,
  type ShopNavGroup,
  type ShopNavItem,
} from "./shop-nav-registry";

/** Значения счётчиков; поля нет — у роли нет права на раздел. */
export type NavCounters = Partial<Record<NavCounterKey, number>>;

/** Режим меню: обычный кабинет или экран кладовщика (SC30). */
export type ShopNavMode = "full" | "warehouse";

/** Меню, которое видит сотрудник. */
export interface VisibleShopNav {
  mode: ShopNavMode;
  /** «Сводка» — нет в режиме кладовщика. */
  home: ShopNavItem | null;
  groups: ShopNavGroup[];
  /** Пункт «Настройки» — если доступен хотя бы один раздел. */
  settings: ShopNavItem | null;
  /** Разделы «Настроек», доступные сотруднику. */
  settingsHub: SettingsHubGroup[];
}

/** Роль кладовщика (W-UX1, phase19.30). */
export const WAREHOUSE_ROLE_CODE = "SHOP_WAREHOUSE_MANAGER";
/** Группа меню склада — единственная в режиме кладовщика. */
export const WAREHOUSE_GROUP_ID = "wms";
/** Поиск по меню показывается, когда пунктов больше (uiux-design §3.1). */
export const NAV_SEARCH_MIN_ITEMS = 20;

/** У сотрудника есть все права пункта и хотя бы одно из `anyPermissions`. */
export function canSee(item: ShopNavItem, permissions: readonly string[]): boolean {
  const all = (item.permissions ?? []).every((p) => permissions.includes(p));
  const any = !item.anyPermissions || item.anyPermissions.some((p) => permissions.includes(p));
  return all && any;
}

/**
 * Экран кладовщика (SC30, phase19.30 §5): роль «Складской работник» и права
 * ТОЛЬКО на склад. Сотрудник с дополнительными правами — обычный кабинет.
 */
export function isWarehouseMode(ctx: AccountContext | null): boolean {
  if (!ctx || ctx.contextType !== "SHOP" || ctx.role.code !== WAREHOUSE_ROLE_CODE) return false;
  return ctx.role.permissions.every((p) => p.startsWith("SHOP_WMS_"));
}

/** Разделы «Настроек», доступные по правам (пустые группы убираются). */
export function visibleSettingsHub(permissions: readonly string[]): SettingsHubGroup[] {
  return SHOP_SETTINGS_HUB.map((group) => ({
    ...group,
    items: group.items.filter((item) => canSee(item, permissions)),
  })).filter((group) => group.items.length > 0);
}

/** Меню сотрудника по правам и режиму. */
export function buildShopNav(permissions: readonly string[], mode: ShopNavMode): VisibleShopNav {
  const groups = SHOP_NAV_GROUPS.filter((g) => mode === "full" || g.id === WAREHOUSE_GROUP_ID)
    .map((g) => ({ ...g, items: g.items.filter((item) => canSee(item, permissions)) }))
    .filter((g) => g.items.length > 0);
  if (mode === "warehouse") return { mode, home: null, groups, settings: null, settingsHub: [] };
  const settingsHub = visibleSettingsHub(permissions);
  return {
    mode,
    home: SHOP_NAV_HOME,
    groups,
    settings: settingsHub.length > 0 ? SHOP_NAV_SETTINGS : null,
    settingsHub,
  };
}

/** Все пункты боковой панели одним списком. */
export function navItems(nav: VisibleShopNav): ShopNavItem[] {
  return [
    ...(nav.home ? [nav.home] : []),
    ...nav.groups.flatMap((g) => g.items),
    ...(nav.settings ? [nav.settings] : []),
  ];
}

/** Путь страницы внутри магазина: `/shop/x/orders/5` → `orders/5`. */
export function shopRelativePath(pathname: string, slug: string): string {
  const prefix = `/shop/${slug}`;
  if (!pathname.startsWith(prefix)) return pathname.replace(/^\//, "");
  return pathname.slice(prefix.length).replace(/^\/+|\/+$/g, "");
}

/** Адрес пункта в магазине `slug`. */
export function itemHref(slug: string, item: ShopNavItem): string {
  return item.path ? `/shop/${slug}/${item.path}` : `/shop/${slug}`;
}

function pathCovers(base: string, path: string): boolean {
  if (base === "") return path === "";
  return path === base || path.startsWith(`${base}/`);
}

/**
 * Раздел, к которому относится страница: пункт с самым длинным совпадающим
 * путём (вложенные страницы — к своему разделу: заказ № 5 → «Заказы»).
 * Учитывает пункты меню и разделы «Настроек».
 */
export function findSectionItem(nav: VisibleShopNav, relativePath: string): ShopNavItem | null {
  const candidates = [...navItems(nav), ...nav.settingsHub.flatMap((g) => g.items)];
  let best: ShopNavItem | null = null;
  for (const item of candidates) {
    if (!pathCovers(item.path, relativePath)) continue;
    if (item.excludePaths?.some((p) => pathCovers(p, relativePath))) continue;
    if (!best || item.path.length > best.path.length) best = item;
  }
  return best;
}

/** Первый доступный раздел «Настроек» — его открывает колонка хаба от 1280px. */
export function firstHubItem(hub: readonly SettingsHubGroup[]): ShopNavItem | null {
  return hub[0]?.items[0] ?? null;
}

/** Раздел «Настроек», к которому относится адрес (вложенные страницы — к своему разделу). */
export function findHubItem(hub: readonly SettingsHubGroup[], relativePath: string): ShopNavItem | null {
  let best: ShopNavItem | null = null;
  for (const item of hub.flatMap((g) => g.items)) {
    if (!pathCovers(item.path, relativePath)) continue;
    if (!best || item.path.length > best.path.length) best = item;
  }
  return best;
}

/** Группа, в которой лежит пункт (для раскрытия группы активной страницы). */
export function groupOfItem(nav: VisibleShopNav, item: ShopNavItem | null): string | null {
  if (!item) return null;
  return nav.groups.find((g) => g.items.some((i) => i.id === item.id))?.id ?? null;
}

/** Это раздел страницы «Настройки»? (подсветка пункта «Настройки»). */
export function isSettingsHubItem(nav: VisibleShopNav, item: ShopNavItem | null): boolean {
  return !!item && nav.settingsHub.some((g) => g.items.some((i) => i.id === item.id));
}

/** Сумма счётчиков группы — показывается у свёрнутой группы (SC18). */
export function groupCounterSum(group: ShopNavGroup, counters: NavCounters): number {
  return group.items.reduce((sum, item) => sum + (item.counter ? (counters[item.counter] ?? 0) : 0), 0);
}

/** Нормализация для поиска: регистр и «ё». */
function norm(text: string): string {
  return text.toLocaleLowerCase("ru-RU").replace(/ё/g, "е").trim();
}

/** Найденный пункт и где он лежит («Заказы», «Настройки · Магазин»). */
export interface NavSearchHit {
  item: ShopNavItem;
  where: string;
}

/** Поиск по меню и разделам «Настроек»: подпись или синоним содержит запрос. */
export function searchShopNav(nav: VisibleShopNav, query: string): NavSearchHit[] {
  const q = norm(query);
  if (!q) return [];
  const matches = (item: ShopNavItem) => [item.label, ...(item.keywords ?? [])].some((t) => norm(t).includes(q));
  const pool: NavSearchHit[] = [
    ...(nav.home ? [{ item: nav.home, where: "" }] : []),
    ...nav.groups.flatMap((g) => g.items.map((item) => ({ item, where: g.label }))),
    ...nav.settingsHub.flatMap((g) => g.items.map((item) => ({ item, where: `Настройки · ${g.label}` }))),
  ];
  return pool.filter((hit) => matches(hit.item));
}

/**
 * Куда перейти при смене магазина (uiux-design §2): в тот же раздел, если
 * у сотрудника в новом магазине есть на него права; иначе — на главную
 * (у кладовщика — обзор склада). В конкретную запись не переходим никогда.
 */
export function sameSectionPath(section: ShopNavItem | null, target: AccountContext): string {
  const slug = target.shop?.slug ?? "";
  const nav = buildShopNav(target.role.permissions, isWarehouseMode(target) ? "warehouse" : "full");
  const available = section ? [...navItems(nav), ...nav.settingsHub.flatMap((g) => g.items)] : [];
  const match = section ? available.find((i) => i.id === section.id) : undefined;
  if (match) return itemHref(slug, match);
  return nav.mode === "warehouse" ? `/shop/${slug}/wms` : `/shop/${slug}`;
}
